import dayjs from 'dayjs';

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { FrequencyDto } from '../../../interfaces/ScheduledJobConfiguration';

import {
	USER_TIMEZONE_VALUE,
	WEEK_LENGTH,
	YEAR_LENGTH_IN_WEEKS
} from './caculateNextScheduledDate.constants';
import {
	MonthlyScheduleParams,
	ScheduleConfig,
	WeeklyScheduleParams
} from './caculateNextScheduledDate.types';

dayjs.extend(utc);
dayjs.extend(timezone);

const convertWeekdayToJS = (weekday: number): number => (weekday === WEEK_LENGTH ? 0 : weekday);

const getLastDayOfMonth = (year: number, month: number): number =>
	dayjs(`${year}-${String(month + 1).padStart(2, '0')}-01`)
		.endOf('month')
		.date();

const getTargetDayForMonth = (day: number, year: number, month: number): number | null => {
	if (day === -1) {
		return getLastDayOfMonth(year, month);
	}
	const lastDay = getLastDayOfMonth(year, month);
	return day > lastDay ? null : day;
};

const createDateAtTime = (base: dayjs.Dayjs, hours: number, minutes: number): dayjs.Dayjs =>
	base.hour(hours).minute(minutes).second(0).millisecond(0);

const calculateNextWeeklyScheduledDate = ({
	wdays,
	interval,
	hours,
	minutes,
	timezone
}: WeeklyScheduleParams): Date | null => {
	if (!wdays?.length) {
		return null;
	}

	const now = dayjs().tz(timezone);
	const selectedWeekdays = wdays.map(convertWeekdayToJS);
	const currentWeekday = now.day();

	const sortedWeekdays = [...selectedWeekdays].sort((a, b) => {
		const daysA = (a - currentWeekday + WEEK_LENGTH) % WEEK_LENGTH;
		const daysB = (b - currentWeekday + WEEK_LENGTH) % WEEK_LENGTH;
		return daysA - daysB;
	});

	for (let weekMultiplier = 0; weekMultiplier <= YEAR_LENGTH_IN_WEEKS; weekMultiplier++) {
		const weekOffset = weekMultiplier * interval;

		for (const weekday of sortedWeekdays) {
			const daysUntilWeekday = (weekday - currentWeekday + WEEK_LENGTH) % WEEK_LENGTH;
			const totalDays = daysUntilWeekday + weekOffset * WEEK_LENGTH;
			const targetDate = createDateAtTime(now.add(totalDays, 'day'), hours, minutes);

			if (!targetDate.isBefore(now)) {
				return targetDate.toDate();
			}
		}
	}

	const daysUntilFirstWeekday =
		(sortedWeekdays[0] - currentWeekday + WEEK_LENGTH) % WEEK_LENGTH || WEEK_LENGTH;
	return createDateAtTime(
		now.add(daysUntilFirstWeekday + interval * WEEK_LENGTH, 'day'),
		hours,
		minutes
	).toDate();
};

const isValidMonth = (candidateMonth: number, months: number[] | undefined): boolean => {
	const hasMultipleMonths = months && months.length > 1;

	if (hasMultipleMonths) {
		return months.includes(candidateMonth + 1);
	}

	return true;
};

const calculateNextMonthlyScheduledDate = ({
	mdays,
	months,
	interval,
	hours,
	minutes,
	timezone
}: MonthlyScheduleParams): Date | null => {
	if (!mdays?.length) {
		return null;
	}

	const now = dayjs().tz(timezone);

	// Sort mdays to check days in ascending order
	const sortedMdays = [...mdays].sort((a, b) => {
		if (a === -1) {
			return 1;
		}
		if (b === -1) {
			return -1;
		}
		return a - b;
	});

	// Search up to 24 months ahead to find the next valid date
	for (let monthOffset = 0; monthOffset < 24; monthOffset++) {
		const candidateDate = now.add(monthOffset, 'month');
		const candidateYear = candidateDate.year();
		const candidateMonth = candidateDate.month();

		if (!isValidMonth(candidateMonth, months)) {
			continue;
		}

		for (const day of sortedMdays) {
			const targetDay = getTargetDayForMonth(day, candidateYear, candidateMonth);
			if (targetDay === null) {
				continue;
			}

			const dateStr = `${candidateYear}-${String(candidateMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
			const targetDate = dayjs.tz(dateStr, timezone).second(0).millisecond(0);

			if (!targetDate.isBefore(now)) {
				return targetDate.toDate();
			}
		}
	}

	// Fallback
	const futureDate = now.add(interval, 'month');
	const firstDay =
		mdays[0] === -1 ? getLastDayOfMonth(futureDate.year(), futureDate.month()) : mdays[0];
	return createDateAtTime(futureDate.date(firstDay), hours, minutes).toDate();
};

export const calculateNextScheduledDate = (config: ScheduleConfig): Date | null => {
	if (!config.frequency) {
		return null;
	}

	let hours: number;
	let minutes: number;

	if (config.hours?.[0] !== undefined && config.minutes?.[0] !== undefined) {
		hours = config.hours[0];
		minutes = config.minutes[0];
	} else if (config.minutes?.[0] !== undefined) {
		const totalMinutes = config.minutes[0];
		hours = Math.floor(totalMinutes / 60);
		minutes = totalMinutes % 60;
	} else {
		return null;
	}

	const timezone = config.timezone || USER_TIMEZONE_VALUE;
	const interval = config.interval || 1;

	if (
		config.frequency === FrequencyDto.WEEKLY ||
		config.frequency === FrequencyDto.EVERY_OTHER_WEEK
	) {
		return calculateNextWeeklyScheduledDate({
			wdays: config.wdays || [],
			interval,
			hours,
			minutes,
			timezone
		});
	}

	if (config.frequency === FrequencyDto.MONTHLY) {
		let mdays = config.mdays || [];
		if (config.lastDayOfMonth && !mdays.includes(-1)) {
			mdays = [...mdays, -1];
		}

		return calculateNextMonthlyScheduledDate({
			mdays,
			months: config.months,
			interval,
			hours,
			minutes,
			timezone
		});
	}

	return null;
};
